import { useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";

const FORM_URL = "https://readdy.ai/api/form/db74rqraskivmpnrcfv0";

const services = [
  {
    icon: "ri-movie-2-line",
    title: "剧集资源索引",
    desc: "聚合热门电视剧信息，按类型、地区、年份分类整理，快速定位你想看的作品。",
  },
  {
    icon: "ri-file-text-line",
    title: "剧情介绍梳理",
    desc: "分段落整理剧情脉络，帮你在开播前了解一部剧讲了什么，值不值得追。",
  },
  {
    icon: "ri-user-star-line",
    title: "演员资讯档案",
    desc: "收录主演名单与演员资料，点击即可查看个人简介与参演作品。",
  },
  {
    icon: "ri-newspaper-line",
    title: "影视资讯速递",
    desc: "新剧前瞻、深度剧评与行业观察，让你始终站在话题的第一排。",
  },
];

export default function AboutPage() {
  usePageMeta(
    "关于我们 - 善良的秘书",
    "善良的秘书是一个专注电视剧资源索引与剧情介绍的门户平台，了解我们的网站定位、服务内容与联系方式。"
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("website_alt") as string | null)?.trim();
    if (honeypot) {
      setStatus("success");
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string") body.append(key, value);
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交被识别为异常内容，请检查后重试。"
            : serverMsg || "提交失败，请稍后再试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-12">
      <Breadcrumb crumbs={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <section className="mt-4 mb-10">
        <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950">关于善良的秘书</h1>
        <p className="mt-3 max-w-3xl text-sm md:text-base leading-relaxed text-foreground-600">
          善良的秘书是一个专注电视剧内容的门户平台。我们相信，追剧的乐趣不只在于观看本身，也在于了解——
          这部剧讲了什么故事、由谁出演、有哪些看点。因此我们把散落的信息重新整理，做成你随时可以查阅的资料库。
        </p>
      </section>

      <section className="mb-12">
        <SectionHeading title="我们提供什么" subtitle="围绕追剧需求打造的四个核心服务" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {services.map((s) => (
            <div key={s.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className={`${s.icon} text-xl`}></i>
              </span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">{s.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <SectionHeading title="联系方式" subtitle="欢迎通过以下渠道与我们联系" accent />
          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-100 text-accent-700">
                <i className="ri-mail-line text-lg"></i>
              </span>
              <span>
                <span className="block text-foreground-500">合作与反馈邮箱</span>
                <span className="mt-0.5 block font-medium text-foreground-900">hello@liangshan-mishi.com</span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-100 text-accent-700">
                <i className="ri-weibo-line text-lg"></i>
              </span>
              <span>
                <span className="block text-foreground-500">官方微博</span>
                <span className="mt-0.5 block font-medium text-foreground-900">@善良的秘书官方</span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-100 text-accent-700">
                <i className="ri-time-line text-lg"></i>
              </span>
              <span>
                <span className="block text-foreground-500">在线时间</span>
                <span className="mt-0.5 block font-medium text-foreground-900">周一至周五 10:00 - 18:00</span>
              </span>
            </li>
          </ul>
          <div className="mt-6 rounded-md bg-background-100 p-4 text-sm leading-relaxed text-foreground-600">
            <i className="ri-information-line mr-1.5 text-accent-600"></i>
            若剧集信息有误或有侵权内容需要处理，请通过邮箱联系，我们会在收到后尽快核实处理。
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-6">
          <SectionHeading title="意见反馈" subtitle="填写下面的表单，把想法告诉我们" />
          <form
            data-readdy-form
            id="liangshan-mishi-feedback-form"
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div>
              <label htmlFor="fb-name" className="mb-1.5 block text-sm text-foreground-700">
                昵称
              </label>
              <input
                id="fb-name"
                name="nickname"
                type="text"
                required
                placeholder="怎么称呼你"
                className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="fb-email" className="mb-1.5 block text-sm text-foreground-700">
                邮箱
              </label>
              <input
                id="fb-email"
                name="email"
                type="email"
                required
                placeholder="方便我们回复你"
                className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="fb-type" className="mb-1.5 block text-sm text-foreground-700">
                反馈类型
              </label>
              <select
                id="fb-type"
                name="feedback_type"
                defaultValue="内容建议"
                className="w-full rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 focus:border-primary-300 focus:outline-none cursor-pointer"
              >
                <option value="内容建议">内容建议</option>
                <option value="剧集信息纠错">剧集信息纠错</option>
                <option value="功能反馈">功能反馈</option>
                <option value="合作洽谈">合作洽谈</option>
                <option value="其他">其他</option>
              </select>
            </div>
            <div>
              <label htmlFor="fb-message" className="mb-1.5 block text-sm text-foreground-700">
                反馈内容
              </label>
              <textarea
                id="fb-message"
                name="message"
                required
                maxLength={500}
                rows={4}
                placeholder="请描述你的建议或问题（最多 500 字）"
                className="w-full resize-none rounded-md border border-background-200 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
              ></textarea>
            </div>

            <input
              className="formGuardField"
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === "submitting"}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
              {status === "submitting" ? "提交中..." : "提交反馈"}
            </button>

            {status === "success" && (
              <p className="flex items-center gap-1.5 rounded-md bg-accent-50 px-3 py-2.5 text-sm text-accent-900 animate-soft-fade">
                <i className="ri-checkbox-circle-line"></i>
                感谢你的反馈，我们已经收到，会尽快查看。
              </p>
            )}
            {status === "error" && (
              <p className="flex items-center gap-1.5 rounded-md bg-primary-50 px-3 py-2.5 text-sm text-primary-700 animate-soft-fade">
                <i className="ri-error-warning-line"></i>
                {formError}
              </p>
            )}
          </form>
        </div>
      </section>

      <section className="rounded-lg border border-background-200 bg-primary-900 p-6 md:p-10 text-center">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">准备好开始追剧了吗？</h2>
        <p className="mx-auto mt-3 max-w-xl text-sm md:text-base leading-relaxed text-primary-200">
          从分类筛选到剧集详情，再到演员资料，善良的秘书陪你把每一部好剧都了解透彻。
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/category"
            className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-6 py-2.5 text-sm font-medium text-primary-950 whitespace-nowrap cursor-pointer hover:bg-accent-400 transition-colors"
          >
            <i className="ri-list-check-2"></i>
            浏览剧集分类
          </Link>
          <Link
            to="/news"
            className="inline-flex items-center gap-2 rounded-md border border-background-50/40 px-6 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap cursor-pointer hover:bg-background-50/10 transition-colors"
          >
            <i className="ri-newspaper-line"></i>
            查看影视资讯
          </Link>
        </div>
      </section>
    </div>
  );
}